import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { WindowManagerProvider as Provider, WindowHostProvider } from '@/shared/lib/windows';
import type { OpenWindow, WindowRequest } from '@/shared/lib/windows';
import { cascadeRect, tileRect, type Rect } from '@/shared/ui/FloatingWindow';
import { useHotkeyScope } from '@/shared/lib/hotkeys';
import { DocumentWindow } from '@/widgets/document-window';
import { CashOrderWindow } from '@/widgets/cash-order-window';
import { CategoryWindow } from '@/widgets/category-window';
import { CategoryGroupWindow } from '@/widgets/category-group-window';
import { CategoryCreateWindow } from '@/widgets/category-create-window';
import { RootGroupCreateWindow } from '@/widgets/root-group-create-window';
import { CounterpartyCreateWindow } from '@/widgets/counterparty-create-window';
import { PaymentWindow } from '@/widgets/payment-window';
import { OrderRequestWindow } from '@/widgets/order-request-window';
import { ReportWindow } from '@/widgets/report-window';
import { AiChatWindow } from '@/widgets/ai-chat-window';
import { useLazyGetDocumentQuery } from '@/entities/document';
import { WindowTaskbar } from './WindowTaskbar';

let seq = 0;

interface FocusRequest {
  key: string;
  signal: number;
}

interface ArrangeRequest {
  targets: Record<string, Rect>;
  signal: number;
}

/** Даёт окну знать, свёрнуто ли оно, и принимает его подпись/dirty-флаг/запросы «встать в фокус»
 *  и «переехать в новые координаты» — каждый запрос адресован конкретному ключу окна, но
 *  рассылается всем сразу (общий объект состояния в провайдере), так что фильтрация «это я или не
 *  я» происходит здесь, один раз на окно, а не в каждом потребителе `useWindowHost()`. */
function WindowHostBoundary({
  windowKey,
  minimized,
  onMinimize,
  onLabel,
  onDirty,
  closeAllSignal,
  focusRequest,
  arrangeRequest,
  children,
}: {
  windowKey: string;
  minimized: boolean;
  onMinimize: (key: string) => void;
  onLabel: (key: string, label: string) => void;
  onDirty: (key: string, dirty: boolean) => void;
  closeAllSignal: number;
  focusRequest: FocusRequest | null;
  arrangeRequest: ArrangeRequest | null;
  children: ReactNode;
}) {
  // Подстройка состояния при смене пропса, не эффект (как `wasMinimized` в FloatingWindow) —
  // сигнал/rect берутся из общего на все окна объекта, но применяются только к тому окну, чей
  // ключ назвали; у остальных локальная копия просто не обновляется.
  const [focusSignal, setFocusSignal] = useState(0);
  if (focusRequest && focusRequest.key === windowKey && focusRequest.signal !== focusSignal) {
    setFocusSignal(focusRequest.signal);
  }

  const [arrangeSignal, setArrangeSignal] = useState(0);
  const [arrangeRect, setArrangeRect] = useState<Rect | undefined>(undefined);
  if (arrangeRequest && arrangeRequest.signal !== arrangeSignal && windowKey in arrangeRequest.targets) {
    setArrangeSignal(arrangeRequest.signal);
    setArrangeRect(arrangeRequest.targets[windowKey]);
  }

  const host = useMemo(
    () => ({
      minimized,
      minimize: () => onMinimize(windowKey),
      setLabel: (label: string) => onLabel(windowKey, label),
      setDirty: (dirty: boolean) => onDirty(windowKey, dirty),
      closeAllSignal,
      focusSignal,
      arrangeSignal,
      arrangeRect,
    }),
    [windowKey, minimized, onMinimize, onLabel, onDirty, closeAllSignal, focusSignal, arrangeSignal, arrangeRect],
  );
  return <WindowHostProvider value={host}>{children}</WindowHostProvider>;
}

function keyFor(request: WindowRequest): string {
  if (request.kind === 'category') return request.id ? `category:${request.id}` : `category:new:${(seq += 1)}`;
  if (request.kind === 'categoryGroup') return `categoryGroup:${request.id}`;
  if (request.kind === 'categoryCreate') return `categoryCreate:${request.parentId}`;
  if (request.kind === 'rootGroupCreate') return 'rootGroupCreate';
  if (request.kind === 'counterpartyCreate') return 'counterpartyCreate';
  if (request.kind === 'payment') return request.id ? `payment:${request.id}` : `payment:new:${(seq += 1)}`;
  if (request.kind === 'orderRequest') return `orderRequest:${request.id}`;
  if (request.kind === 'report') return `report:${request.report}`;
  if (request.kind === 'aiChat') return 'aiChat';
  return request.id ? `document:${request.type}:${request.id}` : `document:${request.type}:new:${(seq += 1)}`;
}

/** Renders every open floating window (§1, §8.7, §11.1 — several at once, no page-blocking mask).
 *
 * §1.8/decision 9 «восстановление открытого окна после F5 через `?doc=<id>`»: the *document*
 * window currently open (or last opened, if several are) is mirrored into the URL's `doc` query
 * param — not category/payment/order-request windows, the plan names documents specifically, and
 * those other window kinds don't carry enough state in a bare id to make F5-restore meaningful the
 * same way (a payment/category window's id alone doesn't tell you its type). */
export function WindowManagerProvider({ children }: { children: ReactNode }) {
  const [windows, setWindows] = useState<OpenWindow[]>([]);
  const [minimized, setMinimized] = useState<string[]>([]);
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [dirtyKeys, setDirtyKeys] = useState<Record<string, boolean>>({});
  const [closeAllSignal, setCloseAllSignal] = useState(0);
  const [focusRequest, setFocusRequest] = useState<FocusRequest | null>(null);
  const [arrangeRequest, setArrangeRequest] = useState<ArrangeRequest | null>(null);
  const focusSignalSeq = useRef(0);
  const arrangeSignalSeq = useRef(0);
  const cycleIndexRef = useRef(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const [fetchDocument] = useLazyGetDocumentQuery();
  const restoreAttempted = useRef(false);

  const open = useCallback(
    (request: WindowRequest) => {
      const key = keyFor(request);
      setWindows((prev) => (prev.some((w) => w.key === key) ? prev : [...prev, { key, request }]));
      // Повторное открытие уже открытого (но свёрнутого) окна разворачивает его.
      setMinimized((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : prev));
      if (request.kind === 'document' && request.id) {
        const docId = request.id;
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set('doc', docId);
            return next;
          },
          { replace: true },
        );
      }
      return key;
    },
    [setSearchParams],
  );

  const close = useCallback(
    (key: string) => {
      setWindows((prev) => {
        const closedRequest = prev.find((w) => w.key === key)?.request;
        if (closedRequest?.kind === 'document' && closedRequest.id) {
          const docId = closedRequest.id;
          setSearchParams(
            (sp) => {
              if (sp.get('doc') !== docId) return sp;
              const next = new URLSearchParams(sp);
              next.delete('doc');
              return next;
            },
            { replace: true },
          );
        }
        return prev.filter((w) => w.key !== key);
      });
      setMinimized((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : prev));
      setLabels((prev) => {
        if (!(key in prev)) return prev;
        const { [key]: _removed, ...rest } = prev;
        return rest;
      });
      setDirtyKeys((prev) => {
        if (!(key in prev)) return prev;
        const { [key]: _removed, ...rest } = prev;
        return rest;
      });
    },
    [setSearchParams],
  );

  const minimize = useCallback((key: string) => {
    setMinimized((prev) => (prev.includes(key) ? prev : [...prev, key]));
  }, []);

  const restore = useCallback((key: string) => {
    setMinimized((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : prev));
  }, []);

  const minimizeAll = useCallback(() => {
    setMinimized((prev) => {
      const all = windows.map((w) => w.key);
      return all.every((k) => prev.includes(k)) ? prev : all;
    });
  }, [windows]);

  const setLabel = useCallback((key: string, label: string) => {
    setLabels((prev) => (prev[key] === label ? prev : { ...prev, [key]: label }));
  }, []);

  const setDirty = useCallback((key: string, dirty: boolean) => {
    setDirtyKeys((prev) => (Boolean(prev[key]) === dirty ? prev : { ...prev, [key]: dirty }));
  }, []);

  // Каждое свёрнутое окно само отреагирует своим onClose (см. FloatingWindow) — так закрытие
  // проходит через обычную проверку несохранённых изменений, а не снимает окна из `windows` в
  // обход неё.
  const closeAllMinimized = useCallback(() => setCloseAllSignal((n) => n + 1), []);

  // Открытые (не свёрнутые) окна — то, с чем работают цикл-переключение, каскад и плитка.
  const openKeys = useMemo(() => windows.map((w) => w.key).filter((k) => !minimized.includes(k)), [windows, minimized]);

  const focusWindow = useCallback((key: string) => {
    setFocusRequest({ key, signal: (focusSignalSeq.current += 1) });
  }, []);

  // Ctrl+M/Ctrl+Shift+M — переключение между открытыми окнами по кругу. Не Alt+Tab/Ctrl+Tab
  // буквально: оба перехватываются браузером/ОС раньше, чем событие дойдёт до страницы.
  const cycleWindow = useCallback(
    (direction: 1 | -1) => {
      if (openKeys.length === 0) return;
      cycleIndexRef.current = (cycleIndexRef.current + direction + openKeys.length) % openKeys.length;
      focusWindow(openKeys[cycleIndexRef.current]!);
    },
    [openKeys, focusWindow],
  );

  const cycleBindings = useMemo(
    () => [
      { combo: 'Ctrl+m', description: 'следующее окно', handler: () => cycleWindow(1) },
      { combo: 'Ctrl+Shift+M', description: 'предыдущее окно', handler: () => cycleWindow(-1) },
    ],
    [cycleWindow],
  );
  useHotkeyScope(cycleBindings);

  const arrange = useCallback(
    (layout: (index: number, total: number, viewport: { width: number; height: number }) => Rect) => {
      if (openKeys.length === 0) return;
      const viewport = { width: window.innerWidth, height: window.innerHeight };
      const targets: Record<string, Rect> = {};
      openKeys.forEach((key, index) => {
        targets[key] = layout(index, openKeys.length, viewport);
      });
      setArrangeRequest({ targets, signal: (arrangeSignalSeq.current += 1) });
    },
    [openKeys],
  );

  const cascadeAll = useCallback(() => arrange((index, _total, viewport) => cascadeRect(index, viewport)), [arrange]);
  const tileAll = useCallback(() => arrange((index, total, viewport) => tileRect(index, total, viewport)), [arrange]);

  const focus = useCallback(
    (key: string) => {
      focusWindow(key);
    },
    [focusWindow],
  );

  const manager = useMemo(
    () => ({ windows, open, close, focus, minimized, minimize, restore, minimizeAll, cascadeAll, tileAll }),
    [windows, open, close, focus, minimized, minimize, restore, minimizeAll, cascadeAll, tileAll],
  );
  const taskbarItems = minimized
    .filter((key) => windows.some((w) => w.key === key))
    .map((key) => ({ key, label: labels[key] ?? key, dirty: Boolean(dirtyKeys[key]) }));

  // Restore once per mount (i.e. once per full page load — this provider only remounts on F5,
  // never on in-app navigation between owner pages, since it wraps `<Outlet/>` inside the layout
  // that stays mounted across them). Fetches the document just to learn its `type` — the window
  // itself re-fetches the full document normally once opened.
  useEffect(() => {
    if (restoreAttempted.current) return;
    restoreAttempted.current = true;
    const docId = searchParams.get('doc');
    if (!docId) return;
    fetchDocument(docId)
      .unwrap()
      .then((doc) => {
        open({ kind: 'document', type: doc.type, id: doc.id });
      })
      .catch(() => {
        // Deleted/inaccessible — drop the stale param instead of leaving a dead link in the URL.
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete('doc');
            return next;
          },
          { replace: true },
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs exactly once, deliberately not re-running on dep changes
  }, []);

  function renderWindow(key: string, request: WindowRequest) {
    if (request.kind === 'category') {
      return <CategoryWindow key={key} id={request.id} parentId={request.parentId} onClose={() => close(key)} />;
    }
    if (request.kind === 'categoryGroup') {
      return <CategoryGroupWindow key={key} id={request.id} onClose={() => close(key)} />;
    }
    if (request.kind === 'categoryCreate') {
      return <CategoryCreateWindow key={key} parentId={request.parentId} onClose={() => close(key)} />;
    }
    if (request.kind === 'rootGroupCreate') {
      return <RootGroupCreateWindow key={key} onClose={() => close(key)} />;
    }
    if (request.kind === 'counterpartyCreate') {
      return <CounterpartyCreateWindow key={key} onClose={() => close(key)} />;
    }
    if (request.kind === 'payment') {
      return <PaymentWindow key={key} id={request.id} counterpartyId={request.counterpartyId} onClose={() => close(key)} />;
    }
    if (request.kind === 'orderRequest') {
      return <OrderRequestWindow key={key} id={request.id} onClose={() => close(key)} />;
    }
    if (request.kind === 'report') {
      return <ReportWindow key={key} report={request.report} onClose={() => close(key)} />;
    }
    if (request.kind === 'aiChat') {
      return <AiChatWindow key={key} onClose={() => close(key)} />;
    }
    // §7: ПКО/РКО — документ без строк, редактор архитектурно другой (нет LinesGrid) —
    // отдельный компонент, хотя окно остаётся под тем же kind:'document' (список/F5-restore
    // уже type-agnostic, заводить новый kind незачем).
    if (request.type === 'CASH_IN' || request.type === 'CASH_OUT') {
      return <CashOrderWindow key={key} type={request.type} id={request.id} baseDocumentId={request.baseDocumentId} onClose={() => close(key)} />;
    }
    return (
      <DocumentWindow
        key={key}
        type={request.type}
        id={request.id}
        copyOf={request.copyOf}
        copyOfLabel={request.copyOfLabel}
        returnTemplateFrom={request.returnTemplateFrom}
        onClose={() => close(key)}
      />
    );
  }

  return (
    <Provider value={manager}>
      {children}
      {windows.map(({ key, request }) => (
        <WindowHostBoundary
          key={key}
          windowKey={key}
          minimized={minimized.includes(key)}
          onMinimize={minimize}
          onLabel={setLabel}
          onDirty={setDirty}
          closeAllSignal={closeAllSignal}
          focusRequest={focusRequest}
          arrangeRequest={arrangeRequest}
        >
          {renderWindow(key, request)}
        </WindowHostBoundary>
      ))}
      <WindowTaskbar
        items={taskbarItems}
        hasOpenWindows={openKeys.length > 0}
        onRestore={restore}
        onCloseAll={closeAllMinimized}
        onMinimizeAll={minimizeAll}
        onCascadeAll={cascadeAll}
        onTileAll={tileAll}
      />
    </Provider>
  );
}
